import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Dropdown, DropdownItem, DropdownMenu } from 'components/Dropdown2';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { selectLocaleData } from 'selectors/locale-data';
import { selectIsMobile } from 'selectors/ui';

import MobileActionSheet from 'components/MobileActionSheet';
import { skuMenuSectionMessages } from '../SkuItems.messages';
import messages from '../SkuMenuPage.messages';
import { formatAllMenuHours } from '../SkuMenuPage.helpers';
import SvgIcon from './SvgIcon';
import './MenuSelectorDropdown.scss';

const { block, element } = bem('menu-selector');

const MenuSelectorDropdown = ({ menus, selectedId, onChange, onAddMenu }) => {
  const intl = useIntl();
  const isMobile = useSelector(selectIsMobile);
  const localeData = useSelector(selectLocaleData);
  const { offsetDaysOfWeek } = localeData;
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const selectedMenu = useMemo(
    () => menus.find(menu => menu.id === selectedId) || menus[0] || null,
    [menus, selectedId]
  );

  const selectedMenuHours = useMemo(
    () => formatAllMenuHours(selectedMenu?.raw, intl, offsetDaysOfWeek),
    [selectedMenu, intl, offsetDaysOfWeek]
  );

  const menuHoursById = useMemo(() => {
    const map = {};
    menus.forEach(menu => {
      map[menu.id] = formatAllMenuHours(menu.raw, intl, offsetDaysOfWeek);
    });
    return map;
  }, [menus, intl, offsetDaysOfWeek]);

  const isSelectedMenuDisabled = selectedMenu?.enabled === false;
  const disabledMenuTooltipName = `sku-menu-disabled-${selectedMenu?.id}`;
  const hasMultipleMenus = menus.length > 1;

  if (isMobile) {
    const menuOptions = menus.map(menu => ({
      label: menu.name,
      onClick: () => onChange(menu.id),
    }));

    const addMenuOptions = onAddMenu ? [{ label: intl.formatMessage(messages.addMenu), onClick: onAddMenu }] : [];

    const options = addMenuOptions.length > 0 ? [menuOptions, addMenuOptions] : menuOptions;

    return (
      <div className={block()}>
        <div className={element('header')}>
          <div className={element('title-container')}>
            <span className={element('title', { disabled: isSelectedMenuDisabled })}>{selectedMenu?.name || ''}</span>
            {isSelectedMenuDisabled && (
              <span className={element('disabled-indicator')}>
                <Isvg src={ICONS.eyeWithCross} />
              </span>
            )}
            {hasMultipleMenus && (
              <button
                type="button"
                className={element('trigger')}
                aria-label={intl.formatMessage(messages.menuSelectorLabel)}
                onClick={() => setIsSheetOpen(true)}>
                <span className={element('trigger-arrow')}>
                  <SvgArrow direction="down" theme="dark" />
                </span>
              </button>
            )}
          </div>
        </div>
        {selectedMenuHours && <div className={element('hours')}>{selectedMenuHours}</div>}
        <MobileActionSheet isOpen={isSheetOpen} onClose={() => setIsSheetOpen(false)} options={options} />
      </div>
    );
  }

  const titleRow = (extraContent = null) => (
    <div className={element('title-container')}>
      <span className={element('title', { disabled: isSelectedMenuDisabled })}>{selectedMenu?.name || ''}</span>
      {isSelectedMenuDisabled && (
        <>
          <TooltipToggle
            name={disabledMenuTooltipName}
            className={element('disabled-indicator')}
            aria-label={intl.formatMessage(skuMenuSectionMessages.disabledMenuTooltip)}>
            <Isvg src={ICONS.eyeWithCross} />
          </TooltipToggle>
          <TooltipContent name={disabledMenuTooltipName} place="bottom">
            <Message message={skuMenuSectionMessages.disabledMenuTooltip} />
          </TooltipContent>
        </>
      )}
      {extraContent}
    </div>
  );

  return (
    <div className={block()}>
      <div className={element('header')}>
        {hasMultipleMenus ? (
          <Dropdown attachment="left" contentClassName={element('dropdown-content')}>
            {({ toggleMenu, closeMenu }) => (
              <>
                {titleRow(
                  <button
                    type="button"
                    className={element('trigger')}
                    aria-label={intl.formatMessage(messages.menuSelectorLabel)}
                    onClick={toggleMenu}>
                    <span className={element('trigger-arrow')}>
                      <SvgArrow direction="down" theme="dark" />
                    </span>
                  </button>
                )}
                <DropdownMenu>
                  {menus.map(menu => {
                    const isSelected = selectedMenu?.id === menu.id;
                    const isDisabled = menu.enabled === false;
                    const hours = menuHoursById[menu.id];
                    return (
                      <DropdownItem key={menu.id} isSelected={isSelected} onClick={() => onChange(menu.id)}>
                        <div className={element('item', { disabled: isDisabled })}>
                          <SvgIcon
                            src={ICONS.checkmark}
                            size={20}
                            className={element('item-check', { hidden: !isSelected })}
                          />
                          <div className={element('item-content')}>
                            <span className={element('item-name')}>{menu.name}</span>
                            {hours && <span className={element('item-hours')}>{hours}</span>}
                          </div>
                          {isDisabled && <SvgIcon src={ICONS.eyeWithCross} size={16} className={element('item-eye')} />}
                        </div>
                      </DropdownItem>
                    );
                  })}
                  <When predicate={!!onAddMenu}>
                    <DropdownItem
                      onClick={() => {
                        closeMenu();
                        onAddMenu();
                      }}>
                      <div className={element('add-menu-option')}>
                        <SvgIcon src={ICONS.plus} size={20} className={element('add-menu-plus')} />
                        <Message message={messages.addMenu} />
                      </div>
                    </DropdownItem>
                  </When>
                </DropdownMenu>
              </>
            )}
          </Dropdown>
        ) : (
          titleRow()
        )}
      </div>
      {selectedMenuHours && <div className={element('hours')}>{selectedMenuHours}</div>}
    </div>
  );
};

MenuSelectorDropdown.propTypes = {
  menus: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      enabled: PropTypes.bool,
      raw: PropTypes.shape({
        businessHours: PropTypes.arrayOf(
          PropTypes.shape({
            dayOfWeek: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            startTime: PropTypes.shape({ hours: PropTypes.number, minutes: PropTypes.number }),
            endTime: PropTypes.shape({ hours: PropTypes.number, minutes: PropTypes.number }),
          })
        ),
      }),
    })
  ).isRequired,
  selectedId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onAddMenu: PropTypes.func,
};

export default MenuSelectorDropdown;
